import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBuildingColumns,
  FaCalendarDays,
  FaCheck,
  FaChevronDown,
  FaCircleCheck,
  FaCircleUser,
  FaCopy,
  FaDownload,
  FaEye,
  FaFilePdf,
  FaPaperPlane,
  FaPen,
  FaRegFilePdf,
  FaTrash,
  FaXmark,
} from "react-icons/fa6";
import { DateRange, type Range, type RangeKeyDict } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { apiFetch } from "../lib/api";
import { toast } from "react-toastify";

type Company = {
  id: number;
  name: string;
  gstin?: string;
  email?: string;
};

type BankDetails = {
  id: number;
  account_name?: string;
  bank_name?: string;
  account_number?: string;
  ifsc?: string;
  tag?: string;
  tag_color?: string;
};

type PersonalInfo = {
  id: number;
  pan?: string;
  gst_rate?: number;
  header_name?: string;
  tag?: string;
  tag_color?: string;
};

type Item = {
  description: string;
  amount: number;
};

type InvoiceTag = { id: number; name: string; color: string };
type InvoiceProject = { id: number; name: string };

type InvoiceSummary = {
  id: number;
  invoice_no: string;
  issue_date: string;
  total: number;
  pdf_file: string;
  pdf_url?: string;
  pdf_download_url?: string;
  company_name?: string;
  company_email?: string;
  company_id: number;
  bank_detail_id?: number | null;
  personal_info_id?: number | null;
  bank_account_name?: string | null;
  bank_account_number?: string | null;
  bank_tag?: string | null;
  bank_tag_color?: string | null;
  personal_pan?: string | null;
  personal_tag?: string | null;
  personal_tag_color?: string | null;
  items_json: string;
  status?: string;
  is_paid: number | boolean;
  paid_at?: string | null;
  project_id?: number | null;
  project_name?: string | null;
  tags_json: string;
};

const emptyItem: Item = { description: "", amount: 0 };
const defaultBookmarkColor = "#0f766e";

type DropdownOption = {
  value: string;
  content: ReactNode;
  selectedContent?: ReactNode;
};

function CustomDropdown({
  value,
  options,
  onChange,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm transition hover:border-brand/50 focus:outline-none focus:ring-2 focus:ring-brand/40"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="min-w-0 flex-1">
          {selectedOption?.selectedContent || selectedOption?.content}
        </span>
        <FaChevronDown
          className={`h-3 w-3 flex-none text-slate-400 transition ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-30 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="max-h-72 overflow-auto p-1">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left text-sm transition ${
                    isSelected
                      ? "bg-teal-50 text-ink"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  <span className="min-w-0 flex-1">{option.content}</span>
                  {isSelected && (
                    <FaCheck className="mt-1 h-3.5 w-3.5 flex-none text-brand" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchableCompanyFilter({
  companies,
  value,
  onChange,
}: {
  companies: Company[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  const selected = companies.find((company) => String(company.id) === value);
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return companies;
    return companies.filter((company) =>
      [company.name, company.email, company.gstin]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [companies, query]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((current) => !current);
          setQuery("");
        }}
        className={`flex h-[42px] w-full items-center gap-3 rounded-xl border bg-white px-3 text-left transition ${
          open ? "border-teal-400 ring-2 ring-teal-500/10" : "border-slate-200 hover:border-teal-300"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-teal-50 text-teal-700">
          <FaBuildingColumns className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700">
          {selected?.name || "All companies"}
        </span>
        <FaChevronDown className={`h-3 w-3 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-40 w-full min-w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="border-b border-slate-100 p-3">
            <input
              autoFocus
              className="input"
              placeholder="Type to search companies…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-2" role="listbox">
            <button
              type="button"
              role="option"
              aria-selected={value === "all"}
              onClick={() => {
                onChange("all");
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 rounded-xl p-3 text-left transition ${
                value === "all" ? "bg-teal-50 text-teal-900" : "hover:bg-slate-50"
              }`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <FaBuildingColumns />
              </span>
              <span className="flex-1 text-sm font-semibold">All companies</span>
              {value === "all" && <FaCheck className="text-teal-700" />}
            </button>
            {filtered.map((company) => {
              const isSelected = String(company.id) === value;
              const meta = [company.gstin ? `GSTIN ${company.gstin}` : "", company.email || ""]
                .filter(Boolean)
                .join(" · ");
              return (
                <button
                  key={company.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(String(company.id));
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl p-3 text-left transition ${
                    isSelected ? "bg-teal-50 text-teal-900" : "hover:bg-slate-50"
                  }`}
                >
                  <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-xl text-xs font-bold uppercase ${
                    isSelected ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    {company.name.slice(0, 2)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{company.name}</span>
                    {meta && <span className="block truncate text-xs text-slate-400">{meta}</span>}
                  </span>
                  {isSelected && <FaCheck className="flex-none text-teal-700" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-slate-500">
                No companies match “{query.trim()}”.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CreatablePicker<T extends { id: number; name: string; color?: string }>({
  label, options, selectedIds, multiple = false, onChange, onCreate, colorized = false,
}: {
  label: string; options: T[]; selectedIds: number[]; multiple?: boolean;
  onChange: (ids: number[]) => void; onCreate?: (name: string, color: string) => Promise<unknown>;
  colorized?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [color, setColor] = useState("#0f766e");
  const [creating, setCreating] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const selected = options.filter((option) => selectedIds.includes(option.id));
  const filtered = options.filter((option) => option.name.toLowerCase().includes(query.trim().toLowerCase()));
  const canCreate = Boolean(onCreate && query.trim()) && !options.some((option) => option.name.toLowerCase() === query.trim().toLowerCase());
  return <div className="relative" ref={ref}>
    <div className="input flex min-h-[42px] cursor-pointer items-center justify-between gap-2 text-left" onClick={() => setOpen((current) => !current)}>
      <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
        {selected.length ? selected.map((option) => <button type="button" key={option.id} className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold" style={colorized ? { backgroundColor: `${option.color}20`, color: option.color } : undefined} onClick={(event) => { event.stopPropagation(); onChange(selectedIds.filter((id) => id !== option.id)); }} aria-label={`Remove ${option.name}`} title={`Remove ${option.name}`}>{option.name}<FaXmark className="h-2.5 w-2.5" /></button>) : <span className="text-slate-500">{label}</span>}
      </span><button type="button" className="flex h-6 w-6 flex-none items-center justify-center" onClick={(event) => { event.stopPropagation(); setOpen(!open); }} aria-label={`Choose ${label.toLowerCase()}`} aria-expanded={open}><FaChevronDown className="h-3 w-3 text-slate-400" /></button>
    </div>
    {open && <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 min-w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
      <input autoFocus className="input mb-2" placeholder={`Search or create ${label.toLowerCase()}…`} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={async (event) => {
        if (event.key === "Escape") setOpen(false);
        if (event.key === "Enter" && canCreate && onCreate) { event.preventDefault(); setCreating(true); try { await onCreate(query.trim(), color); setQuery(""); if (!multiple) setOpen(false); } finally { setCreating(false); } }
      }} />
      <div className="max-h-48 overflow-y-auto">
        {!multiple && <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => { onChange([]); setOpen(false); }}>No project</button>}
        {filtered.map((option) => <button key={option.id} type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => {
          onChange(multiple ? (selectedIds.includes(option.id) ? selectedIds.filter((id) => id !== option.id) : [...selectedIds, option.id]) : [option.id]);
          if (!multiple) setOpen(false);
        }}>
          {colorized && <span className="h-3 w-3 rounded-full" style={{ backgroundColor: option.color }} />}
          <span className="flex-1">{option.name}</span>{selectedIds.includes(option.id) && <FaCheck className="text-teal-700" />}
        </button>)}
      </div>
      {canCreate && onCreate && <div className="flex items-center gap-2 border-t border-slate-100 pt-2">
        {colorized && <input type="color" aria-label="New tag color" title="New tag color" value={color} onChange={(event) => setColor(event.target.value)} className="h-8 w-9 cursor-pointer" />}
        <button type="button" disabled={creating} className="min-w-0 flex-1 rounded-lg px-2 py-2 text-left text-sm font-semibold text-teal-700 hover:bg-teal-50" onClick={async () => { setCreating(true); try { await onCreate(query.trim(), color); setQuery(""); if (!multiple) setOpen(false); } finally { setCreating(false); } }}>+ Create “{query.trim()}”</button>
      </div>}
    </div>}
  </div>;
}

export default function InvoicePage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [bankDetailsList, setBankDetailsList] = useState<BankDetails[]>([]);
  const [personalInfoList, setPersonalInfoList] = useState<PersonalInfo[]>([]);
  const [companyId, setCompanyId] = useState<string>("");
  const [bankDetailId, setBankDetailId] = useState<string>("");
  const [personalInfoId, setPersonalInfoId] = useState<string>("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [tags, setTags] = useState<InvoiceTag[]>([]);
  const [projects, setProjects] = useState<InvoiceProject[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [projectIds, setProjectIds] = useState<number[]>([]);
  const [tagFilter, setTagFilter] = useState<number[]>([]);
  const [items, setItems] = useState<Item[]>([{ ...emptyItem }]);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [pdfStatusById, setPdfStatusById] = useState<Record<number, string>>({});
  const [sendingInvoiceId, setSendingInvoiceId] = useState<number | null>(null);
  const [gstRate, setGstRate] = useState<number>(18);
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<"all" | "unpaid" | "paid">("all");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [dateRange, setDateRange] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  const formatAmount = (value: number) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0);

  useEffect(() => {
    apiFetch("/api/companies")
      .then((res) => res.json())
      .then((data) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    apiFetch("/api/bank-details/all")
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setBankDetailsList(Array.isArray(data) ? data : []);
          return;
        }
        throw new Error("all endpoint unavailable");
      })
      .catch(() =>
        apiFetch("/api/bank-details")
          .then((res) => res.json())
          .then((data) => setBankDetailsList(data ? [data] : []))
          .catch(() => setBankDetailsList([]))
      );
  }, []);

  useEffect(() => {
    apiFetch("/api/personal-info/all")
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          const normalized = Array.isArray(data) ? data : [];
          setPersonalInfoList(normalized);
          if (normalized[0] && typeof normalized[0].gst_rate === "number") {
            setGstRate(normalized[0].gst_rate);
          } else {
            setGstRate(18);
          }
          return;
        }
        throw new Error("all endpoint unavailable");
      })
      .catch(() =>
        apiFetch("/api/personal-info")
          .then((res) => res.json())
          .then((data) => {
            const normalized = data ? [data] : [];
            setPersonalInfoList(normalized);
            if (normalized[0] && typeof normalized[0].gst_rate === "number") {
              setGstRate(normalized[0].gst_rate);
            } else {
              setGstRate(18);
            }
          })
          .catch(() => {
            setPersonalInfoList([]);
            setGstRate(18);
          })
      );
  }, []);

  useEffect(() => {
    if (!personalInfoId) {
      if (personalInfoList[0] && typeof personalInfoList[0].gst_rate === "number") {
        setGstRate(personalInfoList[0].gst_rate);
      } else {
        setGstRate(18);
      }
      return;
    }
    const selectedId = Number(personalInfoId);
    const selected = personalInfoList.find((info) => info.id === selectedId);
    if (selected && typeof selected.gst_rate === "number") {
      setGstRate(selected.gst_rate);
    }
  }, [personalInfoId, personalInfoList]);

  const loadInvoices = () => {
    apiFetch("/api/invoices")
      .then((res) => res.json())
      .then((data) => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => setInvoices([]));
  };

  const loadNextInvoiceNo = () => {
    apiFetch("/api/invoice-next")
      .then((res) => res.json())
      .then((data) => setInvoiceNo(data?.invoiceNo || ""))
      .catch(() => setInvoiceNo(""));
  };

  useEffect(() => {
    loadNextInvoiceNo();
    loadInvoices();
    apiFetch("/api/invoice-tags").then((res) => res.json()).then((data) => setTags(Array.isArray(data) ? data : [])).catch(() => setTags([]));
    apiFetch("/api/invoice-projects").then((res) => res.json()).then((data) => setProjects(Array.isArray(data) ? data : [])).catch(() => setProjects([]));
  }, []);

  const saveTag = async (name: string, color: string): Promise<InvoiceTag | null> => {
    try {
      const response = await apiFetch("/api/invoice-tags", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, color }) });
      const tag = await response.json();
      if (!response.ok) throw new Error(tag?.error || "Failed to create tag");
      setTags((current) => current.some((item) => item.id === tag.id) ? current : [...current, tag]);
      return tag;
    } catch (error: any) { toast.error(error?.message || "Failed to create tag"); return null; }
  };
  const createTag = async (name: string, color: string) => {
    const tag = await saveTag(name, color);
    if (tag) setTagIds((current) => current.includes(tag.id) ? current : [...current, tag.id]);
  };
  const updateCardTags = async (invoice: InvoiceSummary, ids: number[]) => {
    try {
      const response = await apiFetch(`/api/invoices/${invoice.id}/tags`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tagIds: ids }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Unable to update tags");
      setInvoices((current) => current.map((item) => item.id === invoice.id ? { ...item, tags_json: JSON.stringify(data.tagIds) } : item));
    } catch (error: any) { toast.error(error?.message || "Unable to update tags"); }
  };
  const createProject = async (name: string) => {
    try {
      const response = await apiFetch("/api/invoice-projects", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }) });
      const project = await response.json();
      if (!response.ok) throw new Error(project?.error || "Failed to create project");
      setProjects((current) => current.some((item) => item.id === project.id) ? current : [...current, project]);
      setProjectIds([project.id]);
    } catch (error: any) { toast.error(error?.message || "Failed to create project"); }
  };
  const invoiceTagIds = (invoice: InvoiceSummary): number[] => {
    try { const ids = JSON.parse(invoice.tags_json || "[]"); return Array.isArray(ids) ? ids : []; }
    catch { return []; }
  };

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + item.amount, 0),
    [items]
  );
  const gst = useMemo(() => subtotal * (gstRate / 100), [subtotal, gstRate]);
  const total = useMemo(() => subtotal + gst, [subtotal, gst]);
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (companyFilter !== "all" && invoice.company_id !== Number(companyFilter)) {
        return false;
      }
      if (tagFilter.length && !tagFilter.some((id) => invoiceTagIds(invoice).includes(id))) return false;
      const paid = Boolean(invoice.is_paid);
      if (paymentFilter === "paid" && !paid) return false;
      if (paymentFilter === "unpaid" && paid) return false;
      if (dateFilterActive && dateRange.startDate && dateRange.endDate) {
        const invoiceDate = new Date(`${invoice.issue_date}T00:00:00`);
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        if (invoiceDate < start || invoiceDate > end) return false;
      }
      return true;
    });
  }, [companyFilter, dateFilterActive, dateRange, invoices, paymentFilter, tagFilter]);

  useEffect(() => {
    const closeDatePicker = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", closeDatePicker);
    return () => document.removeEventListener("mousedown", closeDatePicker);
  }, []);
  const latestBankLabel = useMemo(() => {
    const latest = bankDetailsList[0];
    if (!latest) {
      return "No bank details set";
    }
    return latest.account_number || latest.account_name || latest.bank_name || "No bank details set";
  }, [bankDetailsList]);
  const latestPanLabel = useMemo(() => {
    const latest = personalInfoList[0];
    if (!latest) {
      return "No PAN set";
    }
    return latest.pan || "No PAN set";
  }, [personalInfoList]);
  const latestGstRate = useMemo(() => {
    const latest = personalInfoList[0];
    if (latest && typeof latest.gst_rate === "number") {
      return latest.gst_rate;
    }
    return 18;
  }, [personalInfoList]);
  const selectedBankLabel = useMemo(() => {
    if (!bankDetailId) {
      return latestBankLabel;
    }
    const selected = bankDetailsList.find((bank) => bank.id === Number(bankDetailId));
    if (!selected) {
      return latestBankLabel;
    }
    return (
      selected.account_number ||
      selected.account_name ||
      selected.bank_name ||
      latestBankLabel
    );
  }, [bankDetailId, bankDetailsList, latestBankLabel]);
  const selectedPanLabel = useMemo(() => {
    if (!personalInfoId) {
      return latestPanLabel;
    }
    const selected = personalInfoList.find((info) => info.id === Number(personalInfoId));
    if (!selected) {
      return latestPanLabel;
    }
    return selected.pan || latestPanLabel;
  }, [personalInfoId, personalInfoList, latestPanLabel]);
  const selectedGstRate = useMemo(() => {
    if (!personalInfoId) {
      return latestGstRate;
    }
    const selected = personalInfoList.find((info) => info.id === Number(personalInfoId));
    if (!selected || typeof selected.gst_rate !== "number") {
      return latestGstRate;
    }
    return selected.gst_rate;
  }, [personalInfoId, personalInfoList, latestGstRate]);
  const companyDropdownOptions = useMemo<DropdownOption[]>(() => [
    {
      value: "",
      content: (
        <div className="grid gap-0.5">
          <span className="font-semibold text-slate-700">Select company</span>
          <span className="text-xs text-slate-500">Choose a company for this invoice</span>
        </div>
      ),
      selectedContent: (
        <span className="block truncate font-semibold text-slate-700">Select company</span>
      ),
    },
    ...companies.map((company) => {
      const meta = [
        company.gstin ? `GSTIN ${company.gstin}` : "",
        company.email || "",
      ]
        .filter(Boolean)
        .join(" - ");

      return {
        value: String(company.id),
        content: (
          <div className="grid min-w-0 gap-0.5">
            <span className="font-semibold text-slate-800">{company.name}</span>
            {meta && <span className="truncate text-xs text-slate-500">{meta}</span>}
          </div>
        ),
        selectedContent: (
          <div className="min-w-0">
            <span className="block truncate font-semibold text-slate-700">{company.name}</span>
            {meta && <span className="block truncate text-xs text-slate-500">{meta}</span>}
          </div>
        ),
      };
    }),
  ], [companies]);
  const bankDropdownOptions = useMemo<DropdownOption[]>(() => {
    const currentOption: DropdownOption = {
      value: "",
      content: (
        <div className="grid gap-0.5">
          <span className="font-semibold text-slate-700">Use current bank account</span>
          <span className="text-xs text-slate-500">{latestBankLabel}</span>
        </div>
      ),
      selectedContent: (
        <span className="block truncate font-semibold text-slate-700">
          Use current bank account
        </span>
      ),
    };

    return [
      currentOption,
      ...bankDetailsList.map((bank) => {
        const tag = bank.tag || "Untagged";
        const color = bank.tag_color || defaultBookmarkColor;

        return {
          value: String(bank.id),
          content: (
            <div className="flex min-w-0 items-center gap-2">
              <FaBuildingColumns
                className="h-4 w-4 flex-none"
                style={{ color }}
                aria-hidden="true"
              />
              <span className="truncate font-semibold text-slate-700">{tag}</span>
            </div>
          ),
          selectedContent: (
            <div className="flex min-w-0 items-center gap-2">
              <FaBuildingColumns
                className="h-4 w-4 flex-none"
                style={{ color }}
                aria-hidden="true"
              />
              <span className="truncate font-semibold text-slate-700">{tag}</span>
            </div>
          ),
        };
      }),
    ];
  }, [bankDetailsList, latestBankLabel]);
  const senderProfileDropdownOptions = useMemo<DropdownOption[]>(() => {
    const currentOption: DropdownOption = {
      value: "",
      content: (
        <span className="font-semibold text-slate-700">
          Use current sender profile
        </span>
      ),
      selectedContent: (
        <span className="block truncate font-semibold text-slate-700">
          Use current sender profile
        </span>
      ),
    };

    return [
      currentOption,
      ...personalInfoList.map((info) => {
        const tag = info.tag || "Untagged";
        const color = info.tag_color || defaultBookmarkColor;

        return {
          value: String(info.id),
          content: (
            <div className="flex min-w-0 items-center gap-2">
              <FaCircleUser
                className="h-4 w-4 flex-none"
                style={{ color }}
                aria-hidden="true"
              />
              <span className="truncate font-semibold text-slate-700">{tag}</span>
            </div>
          ),
          selectedContent: (
            <div className="flex min-w-0 items-center gap-2">
              <FaCircleUser
                className="h-4 w-4 flex-none"
                style={{ color }}
                aria-hidden="true"
              />
              <span className="truncate font-semibold text-slate-700">{tag}</span>
            </div>
          ),
        };
      }),
    ];
  }, [personalInfoList]);


  const updateItem = (index: number, updates: Partial<Item>) => {
    setItems((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, ...updates } : item))
    );
  };

  const addItem = () => setItems((prev) => [...prev, { ...emptyItem }]);
  const removeItem = (index: number) =>
    setItems((prev) => prev.filter((_, idx) => idx !== index));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!companyId) {
      setStatus("Select a company before saving the invoice.");
      toast.error("Select a company before saving the invoice.");
      return;
    }
    setStatus(editingId ? "Updating invoice..." : "Saving invoice...");
    setPdfUrl(null);

    try {
      const response = await apiFetch(
        editingId ? `/api/invoices/${editingId}` : "/api/invoices",
        {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyId: Number(companyId),
          bankDetailId: bankDetailId ? Number(bankDetailId) : null,
          personalInfoId: personalInfoId ? Number(personalInfoId) : null,
          issueDate,
          projectId: projectIds[0] || null,
          tagIds,
          items,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to save");
      }

      const message = `Invoice ${data.invoiceNo} saved successfully. You can now generate its PDF.`;
      setStatus(message);
      toast.success(message);
      setEditingId(null);
      setItems([{ ...emptyItem }]);
      setCompanyId("");
      setBankDetailId("");
      setPersonalInfoId("");
      setIssueDate("");
      setTagIds([]);
      setProjectIds([]);
      loadNextInvoiceNo();
      loadInvoices();
      setIsFormOpen(false);
    } catch (error: any) {
      setStatus(error?.message || "Failed to save invoice.");
      toast.error(error?.message || "Failed to save invoice.");
    }
  };

  const handleGeneratePdf = async (invoiceId: number) => {
    setPdfStatusById((prev) => ({ ...prev, [invoiceId]: "Generating PDF..." }));
    try {
      const response = await apiFetch(`/api/invoices/${invoiceId}/pdf`, {
        method: "POST",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to generate PDF");
      }
      setPdfUrl(data.pdfUrl);
      setPdfStatusById((prev) => ({ ...prev, [invoiceId]: "PDF generated." }));
      toast.success("PDF generated.");
      loadInvoices();
    } catch (error: any) {
      setPdfStatusById((prev) => ({
        ...prev,
        [invoiceId]: error?.message || "Failed to generate PDF.",
      }));
      toast.error(error?.message || "Failed to generate PDF.");
    }
  };

  const handleSendInvoice = async (invoice: InvoiceSummary) => {
    if (!invoice.company_email) {
      toast.error("Add an email address to this company before sending.");
      return;
    }
    setSendingInvoiceId(invoice.id);
    setPdfStatusById((prev) => ({
      ...prev,
      [invoice.id]: `Regenerating PDF and sending to ${invoice.company_email}…`,
    }));
    try {
      const response = await apiFetch(`/api/invoices/${invoice.id}/send`, {
        method: "POST",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to send invoice");
      }
      setPdfUrl(data.pdfUrl || null);
      setPdfStatusById((prev) => ({
        ...prev,
        [invoice.id]: `Sent to ${data.email}.`,
      }));
      toast.success(`Invoice sent to ${data.email}.`);
      loadInvoices();
    } catch (error: any) {
      const message = error?.message || "Failed to send invoice.";
      setPdfStatusById((prev) => ({ ...prev, [invoice.id]: message }));
      toast.error(message);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const handlePaymentStatus = async (invoice: InvoiceSummary) => {
    const isPaid = !Boolean(invoice.is_paid);
    try {
      const response = await apiFetch(`/api/invoices/${invoice.id}/payment`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPaid }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "Unable to update payment status");
      }
      setInvoices((current) =>
        current.map((item) =>
          item.id === invoice.id
            ? { ...item, is_paid: isPaid, paid_at: data.paidAt || null }
            : item
        )
      );
      toast.success(`Invoice marked ${isPaid ? "paid" : "unpaid"}.`);
    } catch (error: any) {
      toast.error(error?.message || "Unable to update payment status");
    }
  };

  const handleDelete = async (invoice: InvoiceSummary) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_no}? This cannot be undone.`)) return;
    try {
      const response = await apiFetch(`/api/invoices/${invoice.id}`, { method: "DELETE" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || "Failed to delete invoice");
      setInvoices((current) => current.filter((item) => item.id !== invoice.id));
      setPdfStatusById((current) => {
        const { [invoice.id]: _removed, ...remaining } = current;
        return remaining;
      });
      toast.success(`Invoice ${invoice.invoice_no} deleted.`);
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete invoice.");
    }
  };

  const getInvoicePdfUrl = (invoice: InvoiceSummary) =>
    invoice.pdf_url || "";

  const handleEdit = (invoice: InvoiceSummary) => {
    setIsFormOpen(true);
    setEditingId(invoice.id);
    setInvoiceNo(invoice.invoice_no);
    setCompanyId(String(invoice.company_id));
    setBankDetailId(invoice.bank_detail_id ? String(invoice.bank_detail_id) : "");
    setPersonalInfoId(invoice.personal_info_id ? String(invoice.personal_info_id) : "");
    setIssueDate(invoice.issue_date || "");
    setTagIds(invoiceTagIds(invoice));
    setProjectIds(invoice.project_id ? [invoice.project_id] : []);
    try {
      const parsed = JSON.parse(invoice.items_json || "[]") as any[];
      const normalized = parsed.map((item) => ({
        description: String(item.description || ""),
        amount: Number(
          item.amount ?? (Number(item.quantity || 0) * Number(item.rate || 0))
        ),
      }));
      setItems(normalized.length ? normalized : [{ ...emptyItem }]);
    } catch {
      setItems([{ ...emptyItem }]);
    }
    setStatus(null);
    setPdfUrl(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setItems([{ ...emptyItem }]);
    setCompanyId("");
    setBankDetailId("");
    setPersonalInfoId("");
    setIssueDate("");
    setTagIds([]);
    setProjectIds([]);
    setStatus(null);
    setPdfUrl(null);
    loadNextInvoiceNo();
    setIsFormOpen(false);
  };

  const handleDuplicate = (invoice: InvoiceSummary) => {
    setIsFormOpen(true);
    setEditingId(null);
    setCompanyId(String(invoice.company_id));
    setBankDetailId(invoice.bank_detail_id ? String(invoice.bank_detail_id) : "");
    setPersonalInfoId(invoice.personal_info_id ? String(invoice.personal_info_id) : "");
    setIssueDate(invoice.issue_date || "");
    setTagIds(invoiceTagIds(invoice));
    setProjectIds(invoice.project_id ? [invoice.project_id] : []);
    try {
      const parsed = JSON.parse(invoice.items_json || "[]") as any[];
      const normalized = parsed.map((item) => ({
        description: String(item.description || ""),
        amount: Number(
          item.amount ?? (Number(item.quantity || 0) * Number(item.rate || 0))
        ),
      }));
      setItems(normalized.length ? normalized : [{ ...emptyItem }]);
    } catch {
      setItems([{ ...emptyItem }]);
    }
    setPdfUrl(null);
    setStatus("Invoice copied. Save to create a new invoice.");
    toast.info("Invoice copied. Save to create a new invoice.");
    loadNextInvoiceNo();
  };

  const handleCreate = () => {
    setEditingId(null);
    setItems([{ ...emptyItem }]);
    setCompanyId("");
    setBankDetailId("");
    setPersonalInfoId("");
    setIssueDate("");
    setTagIds([]);
    setProjectIds([]);
    setStatus(null);
    setPdfUrl(null);
    loadNextInvoiceNo();
    setIsFormOpen(true);
  };

  useEffect(() => {
    if (!isFormOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleCancelEdit();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isFormOpen]);

  const getInvoiceGstRateLabel = (invoice: InvoiceSummary) => {
    if (!invoice.personal_info_id) {
      return `${latestGstRate}%`;
    }
    const selectedInfo = personalInfoList.find(
      (info) => info.id === Number(invoice.personal_info_id)
    );
    if (!selectedInfo || typeof selectedInfo.gst_rate !== "number") {
      return `${latestGstRate}%`;
    }
    return `${selectedInfo.gst_rate}%`;
  };

  return (
    <section className="grid gap-6" style={{ fontSize: "105%" }}>
      <div className="card p-6">
        <h2 className="font-display text-xl font-semibold text-ink">
          Invoice Workspace
        </h2>
        <p className="text-sm text-slate-500">
          Enter invoice details, itemized services, and automatically apply {gstRate}% GST.
        </p>
      </div>

      {isFormOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/55 p-4 backdrop-blur-sm sm:p-8"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) handleCancelEdit();
          }}
        >
          <div className="my-auto w-full max-w-5xl overflow-hidden rounded-3xl border border-white/70 bg-slate-50 shadow-2xl">
            <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">
                  {editingId ? "Edit invoice" : "New invoice"}
                </p>
                <h2 className="mt-1 font-display text-xl font-semibold text-ink">
                  {editingId ? `Update ${invoiceNo}` : "Create invoice"}
                </h2>
              </div>
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                onClick={handleCancelEdit}
                aria-label="Close invoice form"
              >
                ×
              </button>
            </div>
        <form className="grid max-h-[calc(100vh-8rem)] gap-6 overflow-y-auto p-6" onSubmit={handleSubmit}>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Invoice Basics</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-600">Invoice No</label>
                <input
                  className="input"
                  value={invoiceNo}
                  readOnly
                  placeholder="INV-001"
                  required
                />
                {!editingId && (
                  <button
                    type="button"
                    className="mt-2 text-xs font-semibold text-brand hover:text-teal-700"
                    onClick={loadNextInvoiceNo}
                  >
                    Refresh number
                  </button>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">Company</label>
                <CustomDropdown
                  value={companyId}
                  options={companyDropdownOptions}
                  onChange={setCompanyId}
                />
                {companies.length === 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    Add a company first from the Company Details page.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">Issue Date</label>
                <input
                  className="input"
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">Project</label>
                <CreatablePicker label="Project" options={projects} selectedIds={projectIds} onChange={setProjectIds} onCreate={createProject} />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-slate-600">Tags</label>
                <CreatablePicker label="Tags" options={tags} selectedIds={tagIds} multiple colorized onChange={setTagIds} onCreate={createTag} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Billing Profile</h3>
              <p className="text-xs text-slate-500 mt-1">
                These values are used for invoice header/footer and payment details.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-600">Bank Account</label>
                <CustomDropdown
                  value={bankDetailId}
                  options={bankDropdownOptions}
                  onChange={setBankDetailId}
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">Sender Profile</label>
                <CustomDropdown
                  value={personalInfoId}
                  options={senderProfileDropdownOptions}
                  onChange={setPersonalInfoId}
                />
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <span className="mr-4">
                Bank: <span className="font-semibold text-slate-700">{selectedBankLabel}</span>
              </span>
              <span className="mr-4">
                PAN: <span className="font-semibold text-slate-700">{selectedPanLabel}</span>
              </span>
              <span>
                GST Rate: <span className="font-semibold text-slate-700">{selectedGstRate}%</span>
              </span>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 grid gap-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Itemized Tasks</h3>
              <button type="button" className="button-secondary" onClick={addItem}>
                Add Item
              </button>
            </div>

            <div className="grid gap-3">
              {items.map((item, index) => (
                <div
                  key={`item-${index}`}
                  className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-3"
                >
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-slate-500">
                      Description
                    </label>
                    <input
                      className="input"
                      value={item.description}
                      onChange={(e) => updateItem(index, { description: e.target.value })}
                      placeholder="Design work"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500">Amount</label>
                    <input
                      className="input"
                      type="number"
                      min={0}
                      step="0.01"
                      value={item.amount}
                      onChange={(e) => updateItem(index, { amount: Number(e.target.value) })}
                      required
                    />
                  </div>
                  <div className="md:col-span-4 flex items-center justify-between text-xs text-slate-500">
                    <span>
                      Amount: {formatAmount(item.amount)}
                    </span>
                    {items.length > 1 && (
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => removeItem(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Subtotal</span>
            <span className="font-semibold text-ink">{formatAmount(subtotal)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-600">GST ({gstRate}%)</span>
            <span className="font-semibold text-ink">{formatAmount(gst)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between text-base">
            <span className="font-semibold text-slate-700">Total</span>
            <span className="font-bold text-ink">{formatAmount(total)}</span>
          </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="button-secondary border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700"
              onClick={handleCancelEdit}
            >
              Cancel
            </button>
            <button
              className="button"
              type="submit"
            >
              Save
            </button>
            {pdfUrl && (
              <a href={pdfUrl} className="button-secondary" target="_blank" rel="noreferrer">
                Open PDF
              </a>
            )}
            {status && <span className="text-sm text-slate-500">{status}</span>}
          </div>
        </form>
          </div>
        </div>
      )}

        <div className="card overflow-visible p-0">
          <div className="sticky top-0 z-10 rounded-t-2xl border-b border-slate-200 bg-white/95 p-6 backdrop-blur">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold text-ink">Invoices</h3>
                <p className="text-xs text-slate-500">{filteredInvoices.length} invoice{filteredInvoices.length === 1 ? "" : "s"}</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className="button-secondary" onClick={loadInvoices}>
                  Refresh
                </button>
                <button type="button" className="button" onClick={handleCreate}>
                  Create Invoice
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-2">
              <label className="text-xs font-semibold text-slate-500">Filter invoices</label>
              <div className="grid items-end gap-3 md:grid-cols-2 xl:grid-cols-[auto_auto_1.4fr_1fr]">
                <div className="order-3 grid gap-1 text-xs font-semibold text-slate-500">
                  <span>Company</span>
                  <SearchableCompanyFilter
                    companies={companies}
                    value={companyFilter}
                    onChange={setCompanyFilter}
                  />
                </div>
                <div className="order-4 grid gap-1 text-xs font-semibold text-slate-500">
                  <span>Tags</span>
                  <CreatablePicker label="All tags" options={tags} selectedIds={tagFilter} multiple colorized onChange={setTagFilter} />
                </div>
                <div className="order-2 grid gap-1">
                  <span className="text-xs font-semibold text-slate-500">Payment</span>
                  <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                    {(["all", "unpaid", "paid"] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setPaymentFilter(value)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize transition ${
                          paymentFilter === value
                            ? "bg-white text-teal-700 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
                <div ref={datePickerRef} className="order-1 relative grid gap-1">
                  <span className="text-xs font-semibold text-slate-500">Issue date</span>
                  <button
                    type="button"
                    onClick={() => setDatePickerOpen((open) => !open)}
                    className={`flex h-[42px] items-center gap-2 rounded-xl border px-3 text-xs font-semibold transition ${
                      dateFilterActive
                        ? "border-teal-300 bg-teal-50 pr-10 text-teal-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-teal-300"
                    }`}
                  >
                    <FaCalendarDays />
                    {dateFilterActive && dateRange.startDate && dateRange.endDate
                      ? `${dateRange.startDate.toLocaleDateString("en-IN")} – ${dateRange.endDate.toLocaleDateString("en-IN")}`
                      : "Any date"}
                  </button>
                  {dateFilterActive && (
                    <button
                      type="button"
                      className="absolute bottom-[9px] right-2 flex h-6 w-6 items-center justify-center rounded-md text-teal-600 transition hover:bg-teal-100 hover:text-teal-800"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDateFilterActive(false);
                        setDatePickerOpen(false);
                      }}
                      title="Clear date range"
                      aria-label="Clear date range"
                    >
                      <FaXmark className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {datePickerOpen && (
                    <div className="absolute left-0 top-[calc(100%+8px)] z-50 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
                      <DateRange
                        className="invoice-date-range"
                        ranges={[dateRange]}
                        onChange={(ranges: RangeKeyDict) => {
                          setDateRange(ranges.selection);
                          setDateFilterActive(true);
                        }}
                        moveRangeOnFirstSelection={false}
                        rangeColors={["#0f766e"]}
                        showDateDisplay={false}
                        months={2}
                        direction="horizontal"
                      />
                      <div className="flex items-center justify-between border-t border-slate-100 p-3">
                        <button
                          type="button"
                          className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                          onClick={() => {
                            setDateFilterActive(false);
                            setDatePickerOpen(false);
                          }}
                        >
                          Clear dates
                        </button>
                        <button
                          type="button"
                          className="button px-3 py-1.5 text-xs"
                          onClick={() => setDatePickerOpen(false)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            {filteredInvoices.length === 0 ? (
              <p className="text-sm text-slate-500">
                {invoices.length === 0
                  ? "No invoices yet."
                  : "No invoices match this filter."}
              </p>
            ) : (
              <ul className="grid gap-4">
                {filteredInvoices.map((invoice) => (
                  <li
                    key={invoice.id}
                    className="relative rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div className="flex flex-col gap-4">
                      <div>
                        <div className="mb-1 flex items-center gap-2">
                          <p className="text-sm font-semibold text-ink">
                            {invoice.invoice_no}
                          </p>
                          <span
                            className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                              invoice.is_paid
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {invoice.is_paid ? "Paid" : "Unpaid"}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {invoice.company_name || "Unknown company"}
                        </p>
                        <div className="mt-3 grid gap-x-6 gap-y-2 text-xs text-slate-600 sm:grid-cols-2 xl:grid-cols-3">
                          <p><span className="font-semibold text-slate-500">Date:</span> {invoice.issue_date}</p>
                          {invoice.project_name && <p><span className="font-semibold text-slate-500">Project:</span> {invoice.project_name}</p>}
                          <p><span className="font-semibold text-slate-500">Total:</span> {formatAmount(Number(invoice.total))}</p>
                          <p><span className="font-semibold text-slate-500">GST rate:</span> {getInvoiceGstRateLabel(invoice)}</p>
                          <p><span className="font-semibold text-slate-500">Bank:</span> {invoice.bank_account_number || invoice.bank_account_name || latestBankLabel}</p>
                          <p><span className="font-semibold text-slate-500">PAN:</span> {invoice.personal_pan || latestPanLabel}</p>
                        </div>
                        <div className="mt-3 max-w-sm">
                          <span className="mb-1 block text-xs font-semibold text-slate-500">Tags</span>
                          <CreatablePicker label="Add tags" options={tags} selectedIds={invoiceTagIds(invoice)} multiple colorized onChange={(ids) => updateCardTags(invoice, ids)} onCreate={async (name, color) => {
                            const tag = await saveTag(name, color);
                            if (tag) await updateCardTags(invoice, [...invoiceTagIds(invoice), tag.id]);
                          }} />
                        </div>
                      </div>
                      <div className="grid gap-3 border-t border-slate-100 pt-4 xl:grid-cols-[1fr_1.35fr_auto]">
                      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                      <span className="basis-full text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Documents</span>
                        <a
                          href={`/api/invoices/${invoice.id}/html`}
                          className="button-secondary invoice-action"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FaEye aria-hidden="true" />
                          Preview
                        </a>
                        <div className="contents">
                          {invoice.status === "draft" ? (
                            <>
                              <span
                                className="button-secondary invoice-action cursor-not-allowed opacity-40"
                                title="Save the invoice to enable PDF generation"
                                aria-label="Save the invoice to enable PDF generation"
                              >
                                <FaFilePdf aria-hidden="true" />
                                Generate PDF
                              </span>
                              <span
                                className="button-secondary invoice-action cursor-not-allowed opacity-40"
                                title="Save the invoice to enable PDF download"
                                aria-label="Save the invoice to enable PDF download"
                              >
                                <FaDownload aria-hidden="true" />
                                Download PDF
                              </span>
                            </>
                          ) : (
                            <>
                              {invoice.pdf_url ? (
                                <a
                                  href={getInvoicePdfUrl(invoice)}
                                  className="button-secondary invoice-action"
                                  target="_blank"
                                  rel="noreferrer"
                                  title="View PDF"
                                  aria-label="View PDF"
                                >
                                  <FaRegFilePdf aria-hidden="true" />
                                  View PDF
                                </a>
                              ) : (
                                <button
                                  type="button"
                                  className="button-secondary invoice-action"
                                  title="Generate PDF"
                                  aria-label="Generate PDF"
                                  onClick={() => handleGeneratePdf(invoice.id)}
                                >
                                  <FaFilePdf aria-hidden="true" />
                                  Generate PDF
                                </button>
                              )}
                              {invoice.pdf_download_url ? (
                                <a
                                  href={invoice.pdf_download_url || getInvoicePdfUrl(invoice)}
                                  className="button-secondary invoice-action"
                                  title="Download PDF"
                                  aria-label="Download PDF"
                                >
                                  <FaDownload aria-hidden="true" />
                                  Download PDF
                                </a>
                              ) : (
                                <span
                                  className="button-secondary invoice-action cursor-not-allowed opacity-40"
                                  title="Generate PDF before downloading"
                                  aria-label="Generate PDF before downloading"
                                >
                                  <FaDownload aria-hidden="true" />
                                  Download PDF
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                      <span className="basis-full text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Invoice</span>
                      <button
                        type="button"
                        className="button-secondary invoice-action"
                        onClick={() => handleEdit(invoice)}
                      >
                        <FaPen aria-hidden="true" />
                        Edit invoice
                      </button>
                      <button
                        type="button"
                        className="button-secondary invoice-action"
                        onClick={() => handleDuplicate(invoice)}
                      >
                        <FaCopy aria-hidden="true" />
                        Duplicate
                      </button>
                      <button
                        type="button"
                        className="button-secondary invoice-action border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50"
                        title={`Delete ${invoice.invoice_no}`}
                        aria-label={`Delete ${invoice.invoice_no}`}
                        onClick={() => handleDelete(invoice)}
                      >
                        <FaTrash aria-hidden="true" />
                        Delete invoice
                      </button>
                      {invoice.status === "draft" ? (
                        <span
                          className="button-secondary invoice-action cursor-not-allowed opacity-40"
                          title="Save the invoice before sending"
                          aria-label="Save the invoice before sending"
                        >
                          <FaPaperPlane aria-hidden="true" />
                          Send invoice
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="button-secondary invoice-action"
                          title={
                            invoice.company_email
                              ? `Send invoice to ${invoice.company_email}`
                              : "Add a company email before sending"
                          }
                          aria-label="Send invoice to company"
                          disabled={sendingInvoiceId === invoice.id}
                          onClick={() => handleSendInvoice(invoice)}
                        >
                          <FaPaperPlane
                            className={`flex-none ${
                              sendingInvoiceId === invoice.id ? "animate-pulse" : ""
                            }`}
                            aria-hidden="true"
                          />
                          Send invoice
                        </button>
                      )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3 xl:self-start">
                      <span className="basis-full text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Payment</span>
                      <button
                        type="button"
                        className={`button-secondary invoice-action ${
                          invoice.is_paid
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : ""
                        }`}
                        onClick={() => handlePaymentStatus(invoice)}
                      >
                        <FaCircleCheck />
                        {invoice.is_paid ? "Mark unpaid" : "Mark paid"}
                      </button>
                      </div>
                        {pdfStatusById[invoice.id] && (
                          <span className="text-xs text-slate-500 xl:col-span-3">
                            {pdfStatusById[invoice.id]}
                          </span>
                        )}
                      </div>
                    </div>
                    {(invoice.bank_tag ||
                      invoice.bank_tag_color ||
                      invoice.personal_tag ||
                      invoice.personal_tag_color) && (
                      <div className="mt-3 flex max-w-full flex-wrap gap-2 border-t border-slate-100 pt-3">
                        {(invoice.bank_tag || invoice.bank_tag_color) && (
                          <span className="inline-flex max-w-full items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                            <FaBuildingColumns
                              className="h-3.5 w-3.5 flex-none"
                              style={{
                                color: invoice.bank_tag_color || defaultBookmarkColor,
                              }}
                              aria-hidden="true"
                            />
                            <span className="truncate">
                              {invoice.bank_tag || "Untagged"}
                            </span>
                          </span>
                        )}
                        {(invoice.personal_tag || invoice.personal_tag_color) && (
                          <span className="inline-flex max-w-full items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                            <FaCircleUser
                              className="h-3.5 w-3.5 flex-none"
                              style={{
                                color: invoice.personal_tag_color || defaultBookmarkColor,
                              }}
                              aria-hidden="true"
                            />
                            <span className="truncate">
                              {invoice.personal_tag || "Untagged"}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
      </div>
    </section>
  );
}
